// Shared by the Cloudflare catch-all function and the client-side router, so a
// route cannot present one canonical to a crawler and another after Vue mounts.
// Keep this module free of DOM and Node types: the Workers build compiles it
// without the DOM lib.

export const SITE_URL = 'https://lopaka.app';

export const DEFAULT_TITLE = 'Graphics editor and image converter for Arduino, ESP32, Adafruit_GFX, u8g2, TFT_eSPI';
export const DEFAULT_DESCRIPTION =
    'UI graphics editor for embedded screens with pixel perfect code builder and export. Best for Arduino, STM32, ESP32. Converts images to uint32 and uint8 variables for Adafruit GFX, u8g2 and TFT_eSPI';
export const DEFAULT_IMAGE = `${SITE_URL}/assets/img/lopaka-preview.jpg`;

export interface PageMetadata {
    title: string;
    description: string;
    canonical: string;
    indexable: boolean;
}

interface RouteMetadata {
    title: string;
    description: string;
    indexable?: false;
}

// Public pages get their own title/description so they stop competing for the
// same snippet; everything behind auth or mid-checkout stays out of the index.
const ROUTE_METADATA: Record<string, RouteMetadata> = {
    '/': {title: DEFAULT_TITLE, description: DEFAULT_DESCRIPTION},
    '/gallery': {
        title: 'Gallery of embedded display UI projects — Lopaka',
        description:
            'Browse community UI designs for OLED, TFT, and e-paper displays. Open any project in the Lopaka editor and generate Arduino, ESP32, or STM32 code from it.',
    },
    '/price': {
        title: 'Pricing and plans — Lopaka',
        description:
            'Compare free Lopaka access with Plus Monthly and Plus Lifetime. Unlock custom fonts, icon packs, animations, private projects, and unlimited screens.',
    },
    '/careers': {
        title: 'Careers at Lopaka',
        description: 'Open roles at Lopaka, the visual UI editor and code generator for embedded displays.',
    },
    // Signed-out visitors are redirected to the homepage from these.
    '/assets': {title: 'Your assets — Lopaka', description: DEFAULT_DESCRIPTION, indexable: false},
    '/login': {title: 'Sign in — Lopaka', description: DEFAULT_DESCRIPTION, indexable: false},
    '/login-expired': {title: 'Sign-in link expired — Lopaka', description: DEFAULT_DESCRIPTION, indexable: false},
    '/projects': {title: 'Your projects — Lopaka', description: DEFAULT_DESCRIPTION, indexable: false},
    '/upgrade': {title: 'Upgrade your plan — Lopaka', description: DEFAULT_DESCRIPTION, indexable: false},
    '/success': {title: 'Payment received — Lopaka', description: DEFAULT_DESCRIPTION, indexable: false},
};

// Detail pages render per-project metadata that is injected server-side or set
// by the view itself once its data arrives, so the router must not overwrite it.
const SELF_MANAGED_PREFIXES = ['/gallery/', '/editor/', '/canvas/', '/careers/', '/redirect/'];

// Routes the SPA router answers without their own metadata entry: checkout
// entry points, the sandbox alias, and the explicit 404 view. They are listed
// so the server can tell "a real page the app renders" from "a URL nothing
// serves", which is the difference between a 200 and a 404.
const UNLISTED_APP_ROUTES = [
    '/buy_plus',
    '/buy_plus_annual',
    '/buy_plus_lifetime',
    '/buy_plus_week',
    '/buy_hobby',
    '/buy_hobby_annual',
    '/sandbox',
    '/404',
];

export function normalizePath(path: string): string {
    const [pathname] = path.split(/[?#]/);
    return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
}

export function canonicalUrlFor(path: string): string {
    const [pathname] = path.split(/[?#]/);
    const trimmed = pathname.replace(/\/+$/, '');
    return `${SITE_URL}${trimmed}`;
}

/**
 * Returns the metadata for a route, or null when the route manages its own head
 * tags. Unknown paths resolve to a non-indexable page so the SPA 404 fallback
 * never presents itself as a canonical, indexable URL.
 */
export function resolvePageMetadata(path: string): PageMetadata | null {
    const normalized = normalizePath(path);

    if (SELF_MANAGED_PREFIXES.some((prefix) => normalized.startsWith(prefix))) return null;

    const route = ROUTE_METADATA[normalized];
    if (route) {
        return {
            title: route.title,
            description: route.description,
            canonical: canonicalUrlFor(normalized),
            indexable: route.indexable !== false,
        };
    }

    return {
        title: DEFAULT_TITLE,
        description: DEFAULT_DESCRIPTION,
        canonical: canonicalUrlFor(normalized),
        indexable: false,
    };
}

/**
 * True when the SPA router has a view for this path. A path that no route
 * matches renders the NotFound view, so the server answers it with a 404
 * instead of a 200 carrying the app shell.
 */
export function isKnownAppRoute(path: string): boolean {
    const normalized = normalizePath(path);

    if (SELF_MANAGED_PREFIXES.some((prefix) => normalized.startsWith(prefix))) return true;
    if (normalized in ROUTE_METADATA) return true;
    return UNLISTED_APP_ROUTES.includes(normalized);
}
